/**
 * Wraps the app in MSAL and exposes the access token.
 *
 * Sign-in is automatic when the user already has a Microsoft session (a loginRedirect
 * that Microsoft completes on its own) — see autoSignIn.ts for the guards that stop it looping. The sign-in
 * button remains as the fallback.
 *
 * When AAD is not configured (no VITE_AAD_CLIENT_ID — i.e. local development before
 * the app registration exists) this falls through to an unauthenticated mode so the
 * app still runs against a local bundle. That bypass is DEV-ONLY and refuses to
 * engage in a production build.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import type { AccountInfo } from '@azure/msal-browser'
import { msalConfig, loginRequest, isConfigured } from './msalConfig'
import {
  ATTEMPTED_KEY,
  AUTO_FAILED_NOTICE,
  SIGNED_OUT_KEY,
  RENEW_KEY,
  needsInteractiveRenewal,
  autoSignInStep,
  flags,
  inIframe,
  isSilentFrameResponse,
} from './autoSignIn'
import { createIdTokenSource, setIdTokenSource } from './apiFetch'
import { clear as clearCache } from '@/data/cache'
import { forgetDetail } from '@/data/detailFetch'
import { forgetViewAs } from '@/data/viewAs'

interface AuthState {
  account: AccountInfo | null
  token: string | null
  ready: boolean
  error: string | null
  /** Why the sign-in button is showing when sign-in was meant to be automatic. */
  notice: string | null
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  /**
   * A Microsoft Graph access token for the SIGNED-IN user, for the Access page.
   *
   * Silent unless `interactive` — a popup must come from a click, or the browser
   * blocks it. Throws MSAL's error unchanged, so the caller can tell "IT hasn't
   * consented yet" (see isConsentPending) from anything else.
   */
  graphToken: (scopes: string[], interactive?: boolean) => Promise<string>
}

const Ctx = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * The same, or null outside <AuthProvider> — for an optional extra on a page (See my
 * statements) that should simply not appear where there is no sign-in, as in page tests.
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(Ctx)
}

/**
 * Runs unauthenticated. True in local development before the app registration exists,
 * and in a DEMO build (VITE_DEMO) published for review against synthetic data.
 * Never true in a normal production build.
 */
const devBypass =
  (!isConfigured && import.meta.env.DEV) || import.meta.env.VITE_DEMO === '1'

const message = (e: unknown) => (e instanceof Error ? e.message : String(e))

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [ready, setReady] = useState(devBypass)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const pca = useMemo(
    () => (isConfigured ? new PublicClientApplication(msalConfig) : null),
    [],
  )

  useEffect(() => {
    if (!pca) {
      if (!devBypass) setError('AAD is not configured. Set VITE_AAD_CLIENT_ID.')
      return
    }
    const framed = inIframe()
    // MSAL's hidden silent-sign-in frame, carrying the response: the parent window
    // reads it. This copy must not start MSAL, or it could consume the response first.
    if (isSilentFrameResponse(framed, window.location.hash)) return
    let cancelled = false
    let leaving = false
    void (async () => {
      try {
        await pca.initialize()

        // Who is signed in: back from a redirect, cached, or — true SSO, no click —
        // an existing Microsoft session. A failure here is shown on the sign-in card,
        // beside the button, rather than as a dead end; ATTEMPTED stays set, so a
        // failed automatic sign-in never redirects again.
        let acct: AccountInfo | null
        try {
          const result = await pca.handleRedirectPromise()
          // StrictMode runs this effect twice in dev; only the live run may redirect.
          if (cancelled) return
          acct = result?.account ?? pca.getAllAccounts()[0] ?? null

          const attempted = flags.get(ATTEMPTED_KEY)
          const step = autoSignInStep({
            signedIn: acct !== null,
            attempted,
            signedOut: flags.get(SIGNED_OUT_KEY),
            inIframe: framed,
            storageOk: flags.ok(),
          })
          if (step === 'try') {
            // Straight to the redirect; no ssoSilent. ssoSilent loads this page in a
            // hidden iframe, which X-Frame-Options: DENY (staticwebapp.config.json)
            // refuses — so it only ever waited out MSAL's 10s frame timeout first. With
            // a Microsoft session the redirect comes straight back, still with no click.
            flags.set(ATTEMPTED_KEY)
            leaving = true
            await pca.loginRedirect(loginRequest)
            return
          } else if (step === 'manual' && attempted) {
            setNotice(AUTO_FAILED_NOTICE)
          }
        } catch (e) {
          if (!cancelled) setNotice(message(e))
          return
        }

        if (cancelled) return
        setAccount(acct)
        if (acct) {
          flags.clear(ATTEMPTED_KEY)
          flags.clear(SIGNED_OUT_KEY)
          pca.setActiveAccount(acct)
          const t = await pca
            .acquireTokenSilent({ ...loginRequest, account: acct })
            .catch((e) => {
              if (e instanceof InteractionRequiredAuthError || needsInteractiveRenewal(e)) return null
              throw e
            })
          // A saved sign-in that can't be renewed silently (expired, and the iframe
          // fallback is refused): renew by redirect, ONCE. RENEW_KEY stops a loop if the
          // redirect also comes back without a token; then the sign-in card shows.
          if (!t && !framed && !flags.get(RENEW_KEY) && !cancelled) {
            flags.set(RENEW_KEY)
            leaving = true
            await pca.loginRedirect({ ...loginRequest, loginHint: acct.username })
            return
          }
          if (t) flags.clear(RENEW_KEY)
          // The ID TOKEN, never the access token. With the default scopes MSAL
          // returns an access token issued for Microsoft Graph, and Graph stamps a
          // `nonce` into the JWT header precisely so that nobody except Graph can
          // verify the signature. api/shared/auth.ts therefore rejects it and every
          // request comes back 401 — which is correct behaviour on its part.
          //
          // The ID token is the one minted for this client, carries `tid`, `oid` and
          // `preferred_username`, and verifies against the tenant's published keys.
          // The header is called X-Id-Token for this reason.
          if (!cancelled) {
            setToken(t?.idToken ?? null)
            // Requests send whatever this source returns, not `token` above, which is
            // never updated after sign-in — see apiFetch.ts for why.
            setIdTokenSource(
              createIdTokenSource(t?.idToken ?? null, () =>
                pca
                  .acquireTokenSilent({ ...loginRequest, account: acct, forceRefresh: true })
                  .then((r) => r.idToken || null),
              ),
            )
          }
        }
      } catch (e) {
        if (!cancelled) setError(message(e))
      } finally {
        // Leaving for Microsoft: stay on "Signing in…" rather than flash the button.
        if (!cancelled && !leaving) setReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [pca])

  const value: AuthState = {
    account,
    token,
    ready,
    error,
    notice,
    signIn: async () => {
      if (!pca) return
      setNotice(null)
      flags.clear(SIGNED_OUT_KEY)
      await pca.loginRedirect(loginRequest)
    },
    signOut: async () => {
      if (!pca) return
      // Before the redirect: the bundle (named payroll) and the detail rows (notes that
      // can name patients) must not stay on a shared machine after its user leaves.
      forgetDetail()
      forgetViewAs()
      setIdTokenSource(null)
      await clearCache()
      // So coming back from Microsoft shows the button, not an automatic sign-in.
      flags.set(SIGNED_OUT_KEY)
      await pca.logoutRedirect()
    },
    graphToken: async (scopes, interactive = false) => {
      if (!pca || !account) throw new Error('Not signed in')
      const request = { scopes, account }
      const result = interactive
        ? await pca.acquireTokenPopup(request)
        : await pca.acquireTokenSilent(request)
      return result.accessToken
    },
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** True when running unauthenticated against a local bundle. Never true in a build. */
export const isDevBypass = devBypass
